import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SettingKey } from 'config'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getSetting, updateSetting } from 'services'
import { Select } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

const requireFrom = {
  submitLoan: 'Submit Loan',
  submitSetup: 'Submit to LoanSetup',
  submitConditions: 'Submit Conditions',
}

/** Force restructure release on status change, or leave it alone. */
export const restructureChangeToOptions = {
  on: 'On',
  off: 'Off',
  noChange: 'No Change',
}

export type RestructureChangeTo = keyof typeof restructureChangeToOptions

/** Normalize legacy `changeToOff` boolean into On / Off / No Change. */
export const resolveRestructureChangeTo = (
  item: { changeTo?: string; changeToOff?: boolean } | undefined,
  statusKey: string,
): RestructureChangeTo => {
  if (item?.changeTo === 'on' || item?.changeTo === 'off' || item?.changeTo === 'noChange') {
    return item.changeTo
  }
  if (item?.changeToOff === true) return 'off'
  if (item?.changeToOff === false) return 'noChange'
  return statusKey === 'appSigned' ? 'off' : 'noChange'
}

export const RestructureLoanRelease = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<any>({})
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getSetting(SettingKey.RESTRUCTURE_LOAN_RELEASE)
      .then(async ({ value }) => {
        const content = JSON.parse(value || '{}')
        // Migrate legacy changeToOff → changeTo so the UI/API share one field.
        const migrated: Record<string, any> = {}
        let changed = false
        Object.keys(content).forEach((statusKey) => {
          const item = { ...(content[statusKey] || {}) }
          const changeTo = resolveRestructureChangeTo(item, statusKey)
          if (item.changeTo !== changeTo) {
            item.changeTo = changeTo
            changed = true
          }
          if ('changeToOff' in item) {
            delete item.changeToOff
            changed = true
          }
          migrated[statusKey] = item
        })
        if (cancelled) return
        setData(migrated)
        if (changed) {
          await updateSetting(SettingKey.RESTRUCTURE_LOAN_RELEASE, JSON.stringify(migrated))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const onSync = async (_data: any) => {
    setLoading(true)
    await updateSetting(SettingKey.RESTRUCTURE_LOAN_RELEASE, JSON.stringify(_data))
    setLoading(false)
  }

  const onChange = (key: string, value: any, obj: string) => {
    let temp = cloneDeep(data)
    if (temp[key] === undefined) temp[key] = {}
    temp[key][obj] = value
    if (obj === 'changeTo' && 'changeToOff' in temp[key]) delete temp[key].changeToOff
    setData(temp)
    onSync(temp)
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className="w-fit mt-4">
        <table className="w-full text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Loan Status', 'Require From?', 'Loan App Sign Required?', 'Change to'].map((v) => (
                <th scope="col" className="p-3 border" key={`header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(loanGlobalStatus).map((key, index) => {
              const item = data[key] || {}
              return (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="p-2 border">{loanGlobalStatus[key]}</td>
                  <td className="p-2 border">
                    <div className="flex justify-center -mb-4">
                      <Select
                        id={`requireFrom-${key}`}
                        size={3}
                        title=""
                        options={requireFrom}
                        value={item?.requireFrom}
                        hasDefaultOption={true}
                        onChange={(value) => onChange(key, value, 'requireFrom')}
                      />
                    </div>
                  </td>
                  <td className="p-2 border">
                    <div className="flex justify-center">
                      <RenderInput
                        Key={key}
                        input={{
                          title: '',
                          inputType: 'check',
                          error: '',
                          value: item?.signRequired,
                        }}
                        onChange={(key: string, value: any) => onChange(key, value, 'signRequired')}
                      />
                    </div>
                  </td>
                  <td className="p-2 border">
                    <div className="flex justify-center -mb-4">
                      <Select
                        id={`changeTo-${key}`}
                        size={3}
                        title=""
                        options={restructureChangeToOptions}
                        value={resolveRestructureChangeTo(item, key)}
                        hasDefaultOption={false}
                        onChange={(value) => onChange(key, value, 'changeTo')}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
